/**
 * PwaRoleSelect — lightweight launch screen shown ONLY inside the installed PWA
 * when there is no active session. Two large targets: Continue as User or
 * Continue as Lawyer. The choice is persisted to localStorage so future
 * launches default to that role while still offering a "Change role" link.
 *
 * Business rule (from product): the public website experience is unchanged.
 * This screen never renders in a normal browser tab.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserRound, Briefcase, ChevronRight, ArrowLeftRight } from "lucide-react";
import { LAWYER_BASE } from "@lawyer/constants/route";

const STORAGE_KEY = "advoset:pwa:preferredRole";

function readStoredRole() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "user" || v === "lawyer" ? v : null;
  } catch (_) { return null; }
}

function saveRole(role) {
  try { localStorage.setItem(STORAGE_KEY, role); } catch (_) { /* storage disabled — ignore */ }
}

const ROLES = [
  {
    key: "user",
    label: "Continue as User",
    tagline: "Ask a legal question, find a lawyer, book a consultation.",
    icon: UserRound,
    goTo: "/",                         // Home hosts the user login modal via login() call below
    style: "from-[#7c5a17] to-[#B48A2A]",
  },
  {
    key: "lawyer",
    label: "Continue as Lawyer",
    tagline: "Manage bookings, availability and payouts.",
    icon: Briefcase,
    goTo: `${LAWYER_BASE}/login`,
    style: "from-[#0A2540] to-[#0E3454]",
  },
];

export default function PwaRoleSelect({ onLoginRequested }) {
  const navigate = useNavigate();
  const stored = useMemo(() => readStoredRole(), []);
  // First-time launch: default the highlight to "user" per product spec.
  // If the user has picked before, honour that stored value instead.
  const [defaultRole, setDefaultRole] = useState(stored || "user");
  const isDefaultFallback = !stored; // true when we're only defaulting to "user", not remembering

  useEffect(() => {
    // If a role was remembered, don't auto-navigate — this screen still
    // shows so users can switch, but the remembered choice is visually
    // pre-highlighted. Product asked for "default to that role while still
    // providing a Change Role option".
  }, [stored]);

  const pick = (role) => {
    saveRole(role.key);
    setDefaultRole(role.key);
    if (role.key === "user") {
      // Home hosts the login modal; navigate there and pop it open.
      navigate(role.goTo, { replace: true });
      // Nudge the LoginModal to open on next tick — Home reads a query flag.
      setTimeout(() => {
        try {
          if (typeof onLoginRequested === "function") onLoginRequested();
        } catch (_) { /* login modal open is best-effort */ }
      }, 100);
    } else {
      navigate(role.goTo, { replace: true });
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0A2540] text-white flex flex-col"
      data-testid="pwa-role-select"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex-1 flex flex-col justify-center px-6 max-w-md w-full mx-auto">
        <div className="mb-10">
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37] text-[#0A2540] font-black text-2xl flex items-center justify-center mb-4" style={{ fontFamily: "Poppins" }}>A</div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Poppins" }}>
            Welcome to Advoset
          </h1>
          <p className="mt-2 text-sm text-white/70">
            How would you like to continue today?
          </p>
        </div>

        <div className="space-y-3">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const isPreferred = defaultRole === r.key;
            return (
              <button
                key={r.key}
                onClick={() => pick(r)}
                data-testid={`pwa-role-${r.key}`}
                className={`group w-full text-left rounded-2xl bg-gradient-to-br ${r.style} p-4 flex items-center gap-3 transition transform active:scale-[0.98] shadow-lg ring-1 ring-white/10 ${
                  isPreferred && !isDefaultFallback ? "ring-2 ring-[#D4AF37]" : ""
                }`}
              >
                <div className="shrink-0 w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-bold">{r.label}</p>
                    {isPreferred && !isDefaultFallback && (
                      <span className="text-[9px] uppercase tracking-widest font-bold bg-[#D4AF37] text-[#0A2540] px-1.5 py-0.5 rounded-full">
                        Last used
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-[12px] text-white/75 leading-snug">{r.tagline}</p>
                </div>
                <ChevronRight size={18} className="shrink-0 opacity-60 group-active:opacity-100" />
              </button>
            );
          })}
        </div>

        {stored && (
          <button
            onClick={() => {
              try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
              setDefaultRole("user");
            }}
            data-testid="pwa-change-role"
            className="mt-6 inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white/90 self-start"
          >
            <ArrowLeftRight size={12} /> Change role for next launch
          </button>
        )}

        <div className="mt-10 text-[11px] text-white/40">
          <Link to="/articles" className="hover:text-white/70">Knowledge Hub</Link>
          <span className="mx-2">·</span>
          <Link to="/lawyers" className="hover:text-white/70">Browse lawyers</Link>
        </div>
      </div>
    </div>
  );
}
